import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';

import { useSheetsCommon } from './CommonProvider';
import { ACTIONS, reducer } from '../helpers/reducers/collections';
import Services from '../helpers/sdkServices';
import { isBuildSheets } from '../helpers/utils';

const initialState = {
  isEnabled: false,
  isLoadingContainer: false,
  loadContainerError: null,
  isLoadingCollections: false,
  loadCollectionError: null,
  collections: [],
  selectedId: null,
};

const CollectionContext = createContext({});

const CollectionProvider = ({ children }) => {
  const projectId = useCurrentProjectId();
  const { sheetContainerType, isPluginEnabled } = useSheetsCommon();
  const [state, dispatch] = useReducer(reducer, initialState);
  // only build supports collection feature
  const isFeatureEnabled = isBuildSheets(sheetContainerType);

  const setSelectedCollectionId = useCallback(
    (id) => dispatch({ type: ACTIONS.SELECT_COLLECTION, payload: { id } }),
    [],
  );

  const getContainer = useCallback(async () => {
    dispatch({ type: ACTIONS.GET_CONTAINER });

    try {
      const res = await Services.Sheets.getSheetsContainer(projectId, {
        sheetContainerType,
      });
      dispatch({
        type: ACTIONS.GET_CONTAINER_SUCCESS,
        payload: res,
      });
    } catch (error) {
      dispatch({ type: ACTIONS.GET_CONTAINER_FAILED, payload: error });
    }
  }, [projectId, sheetContainerType]);

  const getCollections = useCallback(async () => {
    dispatch({ type: ACTIONS.GET_COLLECTIONS });

    try {
      const res = await Services.Sheets.getCollections(projectId, {
        includeSheetsCount: true,
      });
      dispatch({ type: ACTIONS.GET_COLLECTIONS_SUCCESS, payload: res });
    } catch (error) {
      dispatch({ type: ACTIONS.GET_COLLECTIONS_FAILED, payload: error });
    }
  }, [projectId]);

  const retry = useCallback(() => {
    !!state.loadContainerError && getContainer();
    !!state.loadCollectionError && getCollections();
  }, [
    getCollections,
    getContainer,
    state.loadCollectionError,
    state.loadContainerError,
  ]);

  useEffect(() => {
    isPluginEnabled && isFeatureEnabled && getContainer();
  }, [isPluginEnabled, getContainer, isFeatureEnabled, projectId]);

  useEffect(() => {
    state.isEnabled && getCollections();
  }, [getCollections, projectId, state.isEnabled]);

  const value = {
    ...state,
    isFeatureEnabled,
    isEnabled: isFeatureEnabled && state.isEnabled,
    isLoading: state.isLoadingContainer || state.isLoadingCollections,
    hasError: !!state.loadContainerError || !!state.loadCollectionError,
    setSelectedCollectionId,
    retry,
  };

  return (
    <CollectionContext.Provider value={value}>
      {children}
    </CollectionContext.Provider>
  );
};

CollectionProvider.propTypes = {
  children: PropTypes.node,
};

CollectionProvider.displayName = 'CollectionProvider';

const useCollection = () => useContext(CollectionContext);

export { CollectionProvider, useCollection };
